import { isEmpty, last } from 'lodash';
import React from 'react';

import { Text } from '@/components/text';
import { ShipmentMilestone } from '@/lib/models/shipment/types';

import { ShipmentMilestoneEvent } from '../event';
import { ShipmentMilestoneTransitEvent } from '../transit-event';

export interface ShipmentMilestoneListProps {
  milestones?: ShipmentMilestone[];
  isInTransit?: boolean;
}

const ShipmentMilestoneList = ({ milestones = [], isInTransit }: ShipmentMilestoneListProps) => {
  if (isEmpty(milestones))
    return (
      <div className="py-16 text-center">
        <Text>No milestone data available</Text>
      </div>
    );

  const groupedMilestones = milestones.reduce<Record<string, ShipmentMilestone[]>>((acc, cur) => {
    const location = cur.locationCode || cur.locationName;
    return {
      ...acc,
      [location]: [...(acc[location] || []), cur],
    };
  }, {});
  const groupedMilestoneKeys = Object.keys(groupedMilestones);
  const lastGroupKey = groupedMilestoneKeys[groupedMilestoneKeys.length - 1];

  return (
    <ul>
      {groupedMilestoneKeys.map((groupKey, index) => {
        const thisGroup = groupedMilestones[groupKey];
        const prevGroup = groupedMilestones[groupedMilestoneKeys[index - 1]];
        const lastMarkerOfPrevGroup = prevGroup && last(prevGroup);

        return thisGroup.map((milestone, i) => {
          const prevMarker = thisGroup[i - 1];
          const isFirstInGroup = i === 0;
          const isLastInGroup = thisGroup.length - 1 === i;
          const isLastMilestone = lastGroupKey === groupKey && isLastInGroup;

          const isPending = !milestone.completed;
          const destinationIsPending = isPending && isFirstInGroup;
          const originIsCompleted = prevMarker ? prevMarker.completed : lastMarkerOfPrevGroup?.completed;
          // Transit marker only appears when in transit and moving between origin and dest
          const shouldShowTransitMarker = isInTransit && destinationIsPending && originIsCompleted;

          return (
            <React.Fragment key={`${milestone.id}_${i}`}>
              {shouldShowTransitMarker && <ShipmentMilestoneTransitEvent />}
              <ShipmentMilestoneEvent
                milestone={milestone}
                isFirstInGroup={isFirstInGroup}
                isLastInGroup={isLastInGroup}
                isLast={isLastMilestone}
              />
            </React.Fragment>
          );
        });
      })}
    </ul>
  );
};

export { ShipmentMilestoneList };
